import { makeAutoObservable, runInAction } from "mobx";
import {
  AccountStats,
  AccountUser,
  CreateAccountUserRequest,
  UpdateAccountUserRequest,
} from "../models/AccountUser";
import { AccountApi } from "../services/AccountApi";
import { UserAccount } from "../models/UserAccount";
import { AccountUserApi } from "../services/AccountUserApi";
import { ApiClientApi } from "../services/ApiClientApi";
import {
  ApiClient,
  CreateApiClientRequest,
  UpdateApiClientRequest,
} from "../models/ApiClient";
import { SuperAdminApi } from "../../admin/services/AdminApiClientApi";
import { RootStore } from "../../shared/stores/RootStore";
import { UserApi } from "../services/UserApi";
import { Survey } from "../../survey/models/Survey";
import { SurveyApi } from "../../survey/services/SurveyApi";
import { Account } from "../models/Account";

const preSelectedAccountUserId = localStorage.getItem(
  "preSelectedAccountUserId",
);

const setPreSelectedAccountUserId = (value: string) => {
  localStorage.setItem("preSelectedAccountUserId", value);
};

export class AccountStore {
  accountUsers: AccountUser[] = [];
  userAccounts: UserAccount[] = [];
  surveys: Survey[] = [];
  selectedUserAccount?: UserAccount;
  account?: Account;
  accountStats?: AccountStats;
  apiClients: ApiClient[] = [];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.account = undefined;
      this.userAccounts = [];
      this.surveys = [];
      this.selectedUserAccount = undefined;
    });
    this.resetIntegrations();
  }

  resetIntegrations() {
    runInAction(() => {
      this.apiClients = [];
    });
  }

  resetSurvey() {
    runInAction(() => {
      this.surveys = [];
    });
  }

  resetAccountManagement() {
    runInAction(() => {
      this.accountUsers = [];
      this.accountStats = undefined;
    });
  }

  async fetchAccount(accountId: string) {
    const response = await AccountApi.getAccount(accountId);
    runInAction(() => {
      this.account = response.data;
    });
  }

  async fetchAccountStats(accountId: string) {
    const response = await AccountApi.getStats(accountId);
    runInAction(() => {
      this.accountStats = response.data;
    });
  }

  async fetchUserAccounts() {
    const { data: accounts } = await UserApi.listAccounts();
    runInAction(() => {
      this.userAccounts = accounts;

      if (preSelectedAccountUserId) {
        this.setSelectedUserAccount(preSelectedAccountUserId);
      } else {
        this.selectedUserAccount = accounts[0];
      }
    });
  }

  async fetchAccountUsers(accountId: string) {
    const response = await AccountApi.listUsers(accountId);
    runInAction(() => {
      this.accountUsers = response.data;
    });
  }

  async createAccountUser(body: CreateAccountUserRequest) {
    return await AccountUserApi.create(body).then((response) => {
      runInAction(() => {
        this.accountUsers = [response.data, ...this.accountUsers];
      });
    });
  }

  async updateAccountUser(
    accountUserId: string,
    body: UpdateAccountUserRequest,
  ) {
    return await AccountUserApi.update(accountUserId, body).then((response) => {
      runInAction(() => {
        this.accountUsers = this.accountUsers.map((au) =>
          au.id === accountUserId ? response.data : au,
        );
      });
    });
  }

  async deleteAccountUser(accountUserId: string) {
    return await AccountUserApi.delete(accountUserId).then(() => {
      runInAction(() => {
        this.accountUsers = this.accountUsers.filter(
          (au) => au.id !== accountUserId,
        );
      });
    });
  }

  setSelectedUserAccount(accountUserId: string) {
    runInAction(() => {
      this.selectedUserAccount = this.userAccounts.find(
        (ua) => ua.id === accountUserId,
      );
      if (this.selectedUserAccount) {
        setPreSelectedAccountUserId(this.selectedUserAccount.id);
      }
    });
  }

  async fetchApiClients(accountId: string) {
    await AccountApi.listApiClients(accountId).then(({ data }) => {
      runInAction(() => {
        this.apiClients = data;
      });
    });
  }

  async fetchAdminApiClients() {
    await SuperAdminApi.getAdminAccountApiClients().then(({ data }) => {
      runInAction(() => {
        this.apiClients = data;
      });
    });
  }

  async createApiClient(body: CreateApiClientRequest) {
    return await ApiClientApi.create(body).then(({ data }) => {
      runInAction(() => {
        this.apiClients = [data, ...this.apiClients];
      });
    });
  }

  async updateApiClient(apiKeyId: string, body: UpdateApiClientRequest) {
    return await ApiClientApi.update(apiKeyId, body).then(({ data }) => {
      runInAction(() => {
        this.apiClients = this.apiClients.map((item) =>
          item.id === apiKeyId ? data : item,
        );
      });
    });
  }

  async deleteApiClient(apiKeyId: string) {
    return await ApiClientApi.delete(apiKeyId).then(() => {
      runInAction(() => {
        this.apiClients = this.apiClients.filter(
          (item) => item.id !== apiKeyId,
        );
      });
    });
  }

  async fetchAccountSurveys(accountId: string) {
    const response = await AccountApi.listSurveys(accountId);
    runInAction(() => {
      this.surveys = response.data;
    });
  }

  async addAccountAccess(surveyId: string) {
    const response = await SurveyApi.postAccountAccess(surveyId);
    runInAction(() => {
      this.surveys = this.surveys.map((p) =>
        p.id === surveyId ? response.data : p,
      );
    });
  }

  async removeFromLibrary(surveyId: string) {
    await SurveyApi.deleteMyAccess(surveyId);

    if (this.selectedUserAccount) {
      this.fetchAccountSurveys(this.selectedUserAccount.accountId);
    }
  }
}
